import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { ChevronDown } from "lucide-react";
import { getRelativeTimeString } from "@/lib/dates";
import type { AuditLogItem as AuditLogItemType } from "../types";
import {
  formatAuditAction,
  formatAuditSource,
  formatResourceType,
  getActionIcon,
  getActionIconColor,
} from "../utils/enum-helpers";
import { AuditLogDiff } from "./audit-log-diff";

type AuditLogItemProps = {
  log: AuditLogItemType;
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

export const AuditLogItem = ({ log }: AuditLogItemProps) => {
  const action = formatAuditAction(log.action);
  const source = formatAuditSource(log.source);
  const isUpdateWithDiff = action === "updated" && log.data?.updated;
  const resourceName = log.resource_display || log.resource_id || "Unknown";
  const resourceType = log.resource_type
    ? formatResourceType(log.resource_type)
    : null;

  const ActionIcon = getActionIcon(log.action);

  const descriptionText = resourceType
    ? `${log.display_name} ${action} ${resourceType} "${resourceName}"`
    : log.display_name;

  const content = (
    <div className="flex items-center justify-between gap-2 p-3 sm:gap-4 sm:p-4">
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <div
                  className={cn(
                    "flex shrink-0 items-center gap-1.5 rounded-full p-1 sm:rounded-md sm:px-2 sm:py-1",
                    getActionIconColor(log.action)
                  )}
                />
              }
            >
              <ActionIcon className="size-4" />
              <span className="hidden font-medium text-xs capitalize sm:inline">
                {action}
              </span>
            </TooltipTrigger>
            <TooltipContent className="sm:hidden">
              <p className="capitalize">{action}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={
                <time
                  className="shrink-0 text-muted-foreground text-xs underline decoration-dotted sm:text-sm"
                  dateTime={log.timestamp}
                />
              }
            >
              {getRelativeTimeString(new Date(log.timestamp))}
            </TooltipTrigger>
            <TooltipContent>
              <p>{formatTimestamp(log.timestamp)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={<span className="min-w-0 truncate text-xs sm:text-sm" />}
            >
              <span
                className={cn(
                  "font-medium",
                  resourceType && "hidden sm:inline"
                )}
              >
                {log.display_name}
              </span>
              {resourceType ? (
                <>
                  <span className="hidden text-muted-foreground sm:inline">
                    {" "}
                    {action}{" "}
                  </span>
                  <span className="font-medium">
                    {resourceType} &ldquo;{resourceName}&rdquo;
                  </span>
                </>
              ) : null}
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{descriptionText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant="secondary">{source}</Badge>
        {isUpdateWithDiff ? (
          <ChevronDown className="size-4 -rotate-90 text-muted-foreground transition-transform group-data-[panel-open]:rotate-0" />
        ) : null}
      </div>
    </div>
  );

  if (!isUpdateWithDiff) {
    return <div className="rounded-lg border">{content}</div>;
  }

  return (
    <Collapsible className="rounded-lg border">
      <CollapsibleTrigger
        render={<button className="group w-full text-left" type="button" />}
      >
        {content}
      </CollapsibleTrigger>
      <CollapsibleContent className="min-w-0 overflow-hidden border-t px-4 pb-4">
        <AuditLogDiff log={log} />
      </CollapsibleContent>
    </Collapsible>
  );
};
